<template>
  <v-container>
    <v-card class="mx-auto" min-width="384px" max-width="512px">
      <v-card-title class="blue-grey white--text">
        <h3>[{{groupId}}] group configuration</h3>
      </v-card-title>
      <v-tabs v-model="activeTab">
        <v-tab key="1">Properties</v-tab>
        <v-tab key="2">Members</v-tab>
        <v-tab-item>
          <v-card-text>
            <v-form ref="form" v-model="isFormValid" lazy-validation>
              <v-text-field label="Display name" v-model="displayName" prepend-icon="mdi-label" />
              <v-autocomplete
                label="Owner"
                clearable
                prepend-icon="mdi-account-circle"
                v-model="owner.selected"
                return-object
                chips
                :items="owner.items"
                :loading="owner.loading"
                :search-input.sync="owner.search"
                :rules="owner.rules"
                item-text="text"
                item-value="value"
                dense
              ></v-autocomplete>
            </v-form>
          </v-card-text>
        </v-tab-item>
        <v-tab-item key="2">
          <v-card class="pa-2">
            <v-card-subtitle>
              <h4>
                <v-icon class="pr-6">mdi-account-group</v-icon>User list
              </h4>
            </v-card-subtitle>
            <v-list class="members">
              <v-list-item v-for="(member, index) in memberList" :key="index" class="ma-1">
                <v-list-item-content class="ma-0 pa-0">
                  <v-list-item-title>{{member.text}}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="ma-0 pa-0">
                  <v-btn color="red" @click="groupMemberRemove(index)">
                    <v-icon>mdi-account-remove</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <v-autocomplete
              label="Add a member"
              clearable
              v-model="member.selected"
              return-object
              :items="member.items"
              :loading="member.loading"
              :search-input.sync="member.search"
              item-text="text"
              item-value="value"
              class="px-4"
              @keypress.enter="groupMemberAdd"
              dense
              chips
            >
              <template v-slot:append>
                <v-btn depressed class="ma-0" @click="groupMemberAdd" color="info">
                  <v-icon left>mdi-account-plus</v-icon>
                </v-btn>
              </template>
            </v-autocomplete>
          </v-card>
        </v-tab-item>
      </v-tabs>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn v-if="activeTab !== 1" @click="validate" color="info" :disabled="!isFormValid">
          <v-icon>mdi-content-save</v-icon>Save
        </v-btn>
        <v-btn v-if="activeTab !== 1" @click="close()" color="orange white--text">
          <v-icon>mdi-cancel</v-icon>Cancel
        </v-btn>
        <v-btn v-if="activeTab === 1" @click="close()" color="info">
          <v-icon>mdi-close-circle</v-icon>Close
        </v-btn>
        <v-btn v-if="activeTab !== 1" @click="reset" color="deep-purple white--text">
          <v-icon>mdi-restore</v-icon>Reset
        </v-btn>
        <v-btn @click="remove" color="red white--text">
          <v-icon>mdi-delete</v-icon>Delete
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="groupDelDlg" max-width="380">
      <v-card>
        <v-card-title class="headline">Configuration delete</v-card-title>
        <v-card-text>
          <p>Do-you really want to remove the group {{groupId}}?</p>
          <p>This operation is irreversible!</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="brown" @click="groupDelDlgConfirm()">
            <v-icon>mdi-delete-forever</v-icon>Confirm
          </v-btn>
          <v-btn color="orange" @click="groupDelDlgCancel()">
            <v-icon>mdi-cancel</v-icon>Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.v-list-item {
  min-height: 0px;
}
.v-list.members {
  height: 256px;
  overflow-y: auto;
}
.v-dialog.v-dialog--active .v-card__title {
  cursor: grab;
}
.v-dialog.v-dialog--active .v-card__title:active {
  cursor: grabbing;
}
</style>

<script>
import {
  getTextFromUserRecord,
  vAutoCompDefData,
  autocompleteListUpdate
} from "./../lib";

export default {
  name: "Group",
  props: {
    id: {
      type: String,
      default: null
    },
    tab: {
      type: String,
      default: "summary",
      validator: value => ["summary", "members"].indexOf(value) !== -1
    }
  },
  components: {},
  data: () => ({
    groupId: null,
    displayName: null,
    activeTab: 0,
    tabs: [
      { id: 1, name: "tab1" },
      { id: 2, name: "tab2" }
    ],
    isFormValid: false,
    memberList: [],
    owner: Object.assign(vAutoCompDefData(), {
      rest: {
        path: () => "/users",
        args: {
          sort_asc: true,
          limit: 65535
        },
        mapCallback: e => ({
          value: e.user_id,
          text: getTextFromUserRecord(e)
        })
      },
      rules: [v => !!v || "The owner is required"]
    }),
    member: Object.assign(vAutoCompDefData(), {
      rest: {
        path: () => "/users",
        args: {
          sort_asc: true,
          limit: 65535
        },
        mapCallback: e => ({
          value: e.user_id,
          text: getTextFromUserRecord(e)
        })
      }
    }),
    groupDelDlg: false
  }),
  watch: {
    id: function(value) {
      this.load(value);
    },
    "owner.search": function(value) {
      this.updateList("owner", value);
    },
    "member.search": function(value) {
      this.updateList("member", value);
    },
    tab: function(value) {
      this.setTab(value);
    }
  },
  computed: {},
  created() {
    this.member.rest.filterCallback = e =>
      !this.memberList.some(m => m.value === e.user_id);
    this.setTab(this.tab);
    this.load(this.id);
  },
  methods: {
    setTab(value) {
      if (!value) return;
      switch (value) {
        case "summary":
          this.activeTab = 0;
          break;
        case "members":
          this.activeTab = 1;
          break;
      }
    },
    reset() {
      this.load(this.groupId);
    },
    load(id) {
      this.memberList = [];
      this.groupId = id;
      if (!this.groupId) return;
      this.$axios
        .get("/groups/" + this.groupId)
        .then(result => {
          this.displayName = result.data.display_name;
          this.owner.selected = { value: result.data.owner_id };
          this.owner.search = result.data.owner_id;
          this.$axios
            .get("/groups/" + this.groupId + "/members")
            .then(result => {
              this.memberList = result.data.map(elt => ({
                value: elt.user_id,
                text: getTextFromUserRecord(elt)
              }));
            })
            .catch(result => {
              this.processErrorResponse(result);
              this.close();
            });
        })
        .catch(result => {
          this.processErrorResponse(result);
          this.close();
        });
    },
    groupMemberAdd() {
      const member = this.member.selected;
      if (!member) {
        return;
      }
      this.$axios
        .post("/groups/" + this.groupId + "/members", {
          user_id: member.value
        })
        .then(() => {
          this.memberList.push({
            value: member.value,
            text: member.text
          });
          this.member.selected = null;
          this.member.search = "";
          this.$emit(
            "success",
            `${member.text} successfully added to the member list`
          );
        })
        .catch(result => {
          this.processErrorResponse(result);
        });
    },
    groupMemberRemove(index) {
      const member = this.memberList[index];
      this.$axios
        .delete("/groups/" + this.groupId + "/members/" + member.value)
        .then(() => {
          this.memberList.splice(index, 1);
          this.$emit(
            "success",
            `${member.text} successfully removed from the member list`
          );
        })
        .catch(result => {
          this.processErrorResponse(result);
        });
    },
    processErrorResponse(result) {
      this.$emit("error", result);
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.$axios
          .put("/groups/" + this.groupId, {
            display_name: this.displayName == "" ? null : this.displayName,
            owner_id: this.owner.selected.value
          })
          .then(() => {
            this.$emit("success", "Group successfully updated");
            this.close({
              type: "update",
              data: {
                id: this.groupId,
                owner: this.owner.selected.value,
                displayName: this.displayName == "" ? null : this.displayName
              }
            });
          })
          .catch(result => {
            this.processErrorResponse(result);
          });
      }
    },
    remove() {
      this.groupDelDlgAction = () => {
        this.$axios
          .delete("/groups/" + this.groupId)
          .then(() => {
            this.$emit("success", "Group successfully removed");
            this.close({ type: "delete" });
          })
          .catch(result => {
            this.processErrorResponse(result);
          });
      };
      this.groupDelDlg = true;
    },
    groupDelDlgConfirm() {
      this.groupDelDlgAction();
      this.groupDelDlgAction = null;
      this.groupDelDlg = false;
    },
    groupDelDlgCancel() {
      this.groupDelDlgAction = null;
      this.groupDelDlg = false;
    },
    close(result) {
      this.$emit("close", result);
    },
    updateList(name, filter) {
      autocompleteListUpdate(this, name, filter);
    }
  }
};
</script>
